import { HttpClient, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import jwt_decode, { JwtPayload } from "jwt-decode";
import { merge, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, delay, distinctUntilChanged, filter, map, pairwise, retryWhen, switchMap, tap } from 'rxjs/operators';
import { AuthStore, clientId } from './auth.store';

import { RouteReuseStrategy, Router } from '@angular/router';
import { IUser } from '@common/interfaces/user';
import { AppRouteReuseStrategy } from '@ep-om/core/strategies/app-route-reuse.strategy';
import { resetStores } from '@datorama/akita';
import { AppService, ASSETS_SETTINGS_LOCALSTORAGE_KEY, CLIENT_ID_LOCALSTORAGE_KEY } from '@ep-om/app.service';
import { WebpushNotificationService } from '@ep-om/core/services/wpn.service';
import { genericRetryStrategy } from '@ep-om/utils/genericRetryStrategy';
import { logger } from '@ep-om/utils/logger';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { environment } from 'src/environments/environment';
import { AuthErrorMessage } from '../config/const';
import { AuthQuery } from './auth.query';
import { SystemRole } from '@common/interfaces/permissions';
import { akitaPersistStorage } from '@persistence/akita-persist-storage';
import {
  AUTH_SNAPSHOT_STORAGE_KEY,
  beginSessionTransition,
  completeSessionTransition,
  createCacheEpoch,
  purgeObsoleteStorageInfoKeys,
} from '@persistence/session-cache';

type ImpersonationResponse = {
  accessToken: string;
  refreshToken: string;
  user: IUser;
};

@Injectable({ providedIn: 'root' })
export class AuthService {

  // Oltre questo numero di 401 consecutivi con refresh token ancora valido considero la
  // sessione realmente revocata lato server e sloggo.
  private static readonly MAX_RECOVERABLE_REFRESH_FAILURES = 5;
  private recoverableRefreshFailures = 0;
  // Impulsi per ritentare il refresh dopo un 401 recuperabile (race con un'altra tab).
  private _retryRefresh$ = new Subject<void>();
  private _retryRefreshTimer: any = null;

  private _logout$ = new Subject<void>();
  public loggingOut$ = this._logout$.asObservable();
  private _login$ = new Subject<void>();
  public loggingIn$ = this._login$.asObservable();
  private apiUrl = environment.apiUrl;
  private redirectUrl: string;
  public mainTab$: Observable<boolean>;
  clientId: string = clientId();  

  constructor(
    private http: HttpClient,
    private nzns: NzNotificationService,
    private authStore: AuthStore,
    public authQuery: AuthQuery,
    private wpns: WebpushNotificationService,
    private router: Router,
    private appService: AppService,
    private routeReuse: RouteReuseStrategy,
  ) {

    this.initialize();

  }

  

  async initialize() {
    merge(
      this.appService.mainTab$.pipe(distinctUntilChanged()),
      // Ritentativo dopo un 401 recuperabile: rivaluto la pipeline con lo stato main corrente
      // (il token aggiornato può nel frattempo essere arrivato via storage-sync).
      this._retryRefresh$.pipe(map(() => this.appService.mainTab$.value)),
    ).pipe(
      tap((main) => console.log(main ? '[AUTH] - I Am the main tab' : '[AUTH] - I am NOT the main tab')),
      switchMap(
        (isMain) => {
          if (!isMain) {
            this.recoverableRefreshFailures = 0;
            return of(null);
          }
          return this.authQuery.refreshToken$.pipe(
            tap((token) => console.log('[AUTH] empty token ?')),
            filter(refresh => !!refresh), //what if refresh token never emits??
            switchMap(refresh => {
              if (this.checkTokenExpiration(refresh)) {
                console.log('[AUTH] Refresh token is expired')
                throw Error("Expired Refresh Token")
              }
              const expires = this.getExpirationFromToken(this.authStore.getValue().accessToken);
              let timeout = expires.getTime() - new Date().getTime() - (10 * 1000);
              console.log('[AUTH] token expires in', timeout / 1000, "secs");
              timeout = timeout <= 0 ? 100 : timeout;
              return of(refresh).pipe(delay(timeout));
            }),
            catchError((err, _) => {
              return of(err);
            }),
          )
        }
      ),
      filter(refresh => !!refresh),
      tap(() => console.log('[AUTH] refreshing now')),
      switchMap(refreshToken => this.refresh(refreshToken).pipe(
        catchError((err, _) => {
          return of(err);
        }),
      )),
      // takeUntil(this.authQuery.refreshToken$.pipe(filter(token => !token), distinctUntilChanged())),
      // repeatWhen(() => this.authQuery.refreshToken$.pipe(filter(token => !!token), distinctUntilChanged())),
    ).subscribe({
      next: async (response: HttpErrorResponse | Error | { accessToken: string, refreshToken: string, user: IUser }) => {
        if (response instanceof HttpErrorResponse || response instanceof Error) {
          if (response.message === 'not logged in') {
            return;
          }
          const status = response instanceof HttpErrorResponse ? response.status : undefined;
          const refreshToken = this.authStore.getValue().refreshToken;
          const refreshTokenValid = !!refreshToken && !this.checkTokenExpiration(refreshToken);
          // 401 con refresh token ancora valido = quasi sempre una race: un'altra tab ha
          // ruotato il token oltre la grace window del backend. Non sloggo: il token nuovo
          // arriva via storage-sync e ritento. Sloggo solo se il refresh token è scaduto o
          // se le race si ripetono troppe volte (revoca reale lato server).
          if (status === 401 && refreshTokenValid && this.recoverableRefreshFailures < AuthService.MAX_RECOVERABLE_REFRESH_FAILURES) {
            this.recoverableRefreshFailures++;
            const backoff = Math.min(this.recoverableRefreshFailures * 2000, 10000);
            console.warn('[AUTH] - refresh 401 with valid refresh token, retrying after', backoff, 'ms (attempt', this.recoverableRefreshFailures, ')');
            if (this._retryRefreshTimer) {
              clearTimeout(this._retryRefreshTimer);
            }
            this._retryRefreshTimer = setTimeout(() => this._retryRefresh$.next(), backoff);
            return;
          }
          console.log('[AUTH] - ops during refresh', response)
          // this.nzns.create(
          //   'error',
          //   'Autenticazione fallita, è necessario ripetere il login.',
          //   ''
          // );
          return await this.logout();
        }
        console.log('[AUTH] - new token arrived')
        this.recoverableRefreshFailures = 0;
        this.authStore.update(state => ({
          ...state,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          userAgent: window.navigator.userAgent,
          user: response.user
        }))
      },
      complete() {
        console.log('[AUTH] - completed')
      },
    });

    this.authQuery.loggedIn$.pipe(
      pairwise(),
      tap(async ([beforeLoggedIn, afterLoggedIn]) => {
        if (beforeLoggedIn === true && afterLoggedIn === false) {
          await this.router.navigate(['/login'])
        }
      })
    ).subscribe();
  }

  isLoggedIn() {
    return !!this.authQuery.getValue().accessToken;
  }

  setRedirectUlr(url: string) {
    this.redirectUrl = url;
  }

  refresh(token): Observable<HttpRequest<any>> {
    if (!this.isLoggedIn()) {
      return throwError(new Error('not logged in'))
    }
    return this.http
      .post(`${this.apiUrl}/api/auth/refresh`, { refreshToken: token, clientId: this.authStore.getValue().clientId, userAgent: window.navigator.userAgent })
      .pipe(
        retryWhen<HttpRequest<any>>(genericRetryStrategy({
          maxRetryAttempts: Infinity,
          scalingDuration: 3000,
          excludedStatusCodes: [401],
        })),
      )
  }

  setUserIdFromToken(token: string) {
    const payload = jwt_decode(token) as any;
    this.authStore.update({ userId: payload.userId })
  }

  getExpirationFromToken(token: string): Date {
    const payload = jwt_decode(token) as JwtPayload;
    if (payload.exp)
      return new Date(payload.exp * 1000);
    else
      return new Date(0)
  }

  checkTokenExpiration(token: string, date = new Date()): boolean {
    const expirationDate = this.getExpirationFromToken(token);

    if (expirationDate.valueOf() < date.valueOf())
      return true

    return false;
  }

  async forgotPassword(email: string) {

    try {
      await this.http.post(`${this.apiUrl}/api/auth/forgotpassword`, { email }).toPromise();
      this.nzns.create('success', 'Operazione effettuata.', 'Controllare la posta ...');
    }
    catch (err) {
      this.nzns.create('error', 'Operazione non riuscita.', err.message);
    }

  }

  login({ username = '', password = '' }) {
    this.authStore.setLoading(true);
    this.http
      .post(`${this.apiUrl}/api/auth/login`, { username, password, ...this.authStore.getValue() })
      //.pipe(this.httpPipe)
      .subscribe({
        next: (response: any) => {
          logger.log('Auth Response', response)
          this.authStore.update((state) => ({
            ...state,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            user: response.user
          }));
          this.setUserIdFromToken(this.authStore.getValue().refreshToken);
        },
        complete: async () => {
          this.nzns.create(
            'success',
            'Login effettuato',
            ''
          );
          if (this.redirectUrl) {
            console.log('redirecting to url');
            await this.router.navigateByUrl('/' + this.redirectUrl);
            this.redirectUrl = null;
            this._login$.next();
            return;
          }
          await this.router.navigate(['/']);
          this._login$.next();
        },
        error: (body) => {
          logger.log(body)
          if (body.status === 503) {
            this.appService.setMaintenance(true);
            this.router.navigate(['maintenance']);
          }
          this.checkAuthError(body.error.message, username);
          this._login$.next();
        }
      });
  }

  private async requestImpersonation(username = ''): Promise<ImpersonationResponse | null> {
    this.authStore.setLoading(true);
    try {
      const rv = await this.http.post<ImpersonationResponse>(`${this.apiUrl}/api/auth/impersonate`, { username, ...this.authStore.getValue() }).toPromise();
      return rv;
    } catch (error) {
      if (error.status === 401) {
        this.nzns.create(
          'error',
          'Impersonificazione negata',
          'Non si dispone del ruolo necessario'
        );
      }
      else {
        console.log(error);
        this.nzns.create(
          'error',
          'Impersonificazione negata',
          error.message
        );
      }
      this.authStore.setLoading(false);
      return null;
    }
  }

  /**
   * Cambia identita' come un'unica transizione di sessione. La nuova auth viene preparata nel
   * localStorage per il bootstrap successivo, ma non applicata all'AuthStore corrente: i motori
   * realtime restano quindi disconnessi dopo resetStores e non possono ripartire prima del reload.
   */
  async impersonateAndRestart(username = ''): Promise<void> {
    const response = await this.requestImpersonation(username);
    if (!response) {
      return;
    }

    const nextEpoch = createCacheEpoch();
    const transition = beginSessionTransition(nextEpoch);
    this.authStore.setLoading(false);

    try { await this.wpns.clean(); } catch (e) { }

    this.resetInMemoryAuthState();
    resetStores();
    this.clearReusedRoutes();
    this._logout$.next();

    const resetAuth = this.authStore.getValue();
    const nextAuth = {
      ...resetAuth,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      userId: response.user.id,
      user: response.user,
      projectRole: null,
      userAgent: window.navigator.userAgent,
      cacheEpoch: nextEpoch,
    };

    // Commit sincrono e unico di identita' + epoch. Il persistState auth e' intenzionalmente
    // bypassato: durante la transizione il suo fencing rifiuta tutte le scritture dei vecchi runtime.
    localStorage.setItem(AUTH_SNAPSHOT_STORAGE_KEY, JSON.stringify({ auth: nextAuth, $cache: { auth: false } }));
    purgeObsoleteStorageInfoKeys(nextEpoch);

    try {
      await akitaPersistStorage.prepareForNextEpoch(nextEpoch);
    } catch (error) {
      // Il bootstrap ripete sempre il purge prima di iterate: anche se questa prima passata fallisce,
      // non idratera' mai valori di un'altra epoch. Il reload resta necessario per completare lo switch.
      console.error('[AUTH] pulizia cache durante impersonificazione fallita, ritento al bootstrap', error);
    }

    completeSessionTransition(transition.id);
    window.location.reload();
  }

  checkAuthError(error: string, username?: string) {
    switch (error) {
      case AuthErrorMessage.Username: {
        const isEmail = username.includes('@')
        this.nzns.create(
          'error',
          `${isEmail ? 'Email' : 'Username'} non esistente.`,
          ''
        );
        break;
      }
      case AuthErrorMessage.Password: {
        this.nzns.create(
          'error',
          'Password sbagliata.',
          ''
        );
        break;
      }
      default: {
        this.nzns.create(
          'error',
          'Login fallito.',
          ''
        );
      }
    }
  }

  async register({ email = '', password = '', name = '' }) {
    try {
      await this.http.post(`${this.apiUrl}/api/auth/register`, { email, name, password }).toPromise();
      this.nzns.create('success', 'Operazione effettuata.', 'Controllare la posta ...');
      this.router.navigate(['login']);
    } catch (e) {
      const errorMessage = e.error?.message?.length > 0 ? e.error.message.map(m => ` • ${m}`).join('\n') : e.message;
      this.nzns.create('error', 'Operazione non riuscita.', errorMessage);
    }

  }


  // Pulisce lo stato in-memory legato a refresh/main-tab. Va fatto ad ogni logout (volontario o
  // forzato) per evitare che timer/leadership/contatori della sessione precedente influenzino il
  // login successivo (es. switch utente).
  private resetInMemoryAuthState() {
    this.recoverableRefreshFailures = 0;
    if (this._retryRefreshTimer) {
      clearTimeout(this._retryRefreshTimer);
      this._retryRefreshTimer = null;
    }
    this.appService.resetMainTab();
  }

  // Al logout svuota TUTTO il localStorage (store persistiti, settings, versioni, motore realtime,
  // authStore con token/utente, ...) tenendo solo il client uid: è l'unica cosa con senso da
  // preservare fra una sessione e l'altra (lato backend lo stesso client può loggare utenti diversi).
  private clearLocalStorageExceptClientId() {
    const keep = new Set<string>([CLIENT_ID_LOCALSTORAGE_KEY, ASSETS_SETTINGS_LOCALSTORAGE_KEY]);
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keep.has(key)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach(key => localStorage.removeItem(key));
  }

  private clearReusedRoutes() {
    if (this.routeReuse instanceof AppRouteReuseStrategy) {
      this.routeReuse.clear();
    }
  }

  async logout() {
    try { await this.wpns.clean(); } catch (e) { }
    const clientId = this.authStore.getValue().clientId;
    const accessToken = this.authStore.getValue().accessToken;
    // azzeriamo il token PRIMA del reset così, quando resetStores svuota lo store
    // delle issue, close() vede già l'utente non loggato e non naviga (evita la race)
    this.authStore.update({ accessToken: '' });
    this.resetInMemoryAuthState();
    resetStores()
    this.clearReusedRoutes();
    // Dopo resetStores: rimuovo dal localStorage tutto (incluso authStore appena riscritto dal
    // reset) tranne il client uid.
    this.clearLocalStorageExceptClientId();
    this._logout$.next();
    // token azzerato → AlreadyLoggedInGuard consente /login.
    // Navighiamo esplicitamente: prima la chiusura della issue (via '..') portava
    // indirettamente al login, ora quel percorso è soppresso dalla guardia in close().
    await this.router.navigate(['login']);
    try {
      await this.http
      .post(`${this.apiUrl}/api/auth/logout`,
        { clientId },
        { headers: { authorization: `Bearer ${accessToken}` } }
      ).toPromise()
    } catch (e) {
      console.error('[AUTH SERVICE] - error during logout:', e);
    }
    window.location.reload();
  }

  updateUser(user: Partial<IUser> & Pick<IUser, 'id'>) {
    const state = this.authStore.getValue();
    const loggedUserId = state.userId || state.user?.id;
    // Guardia anti switch-utente: scarta update che non riguardano l'utente attualmente loggato.
    // Es. messaggi 'me'/current_user_updated residui della sessione socket precedente arrivati
    // dopo un logout + login con un altro utente: senza questo check riscriverebbero lo store
    // con il vecchio utente fino al refresh pagina.
    if (loggedUserId && user?.id && user.id !== loggedUserId) {
      console.warn('[AUTH] - ignoring user update for non-logged user', user.id, 'logged:', loggedUserId);
      return;
    }
    this.authStore.update(prev => ({
      ...prev,
      userId: user.id || prev.userId,
      user: {
        ...(prev.user || {}),
        ...user,
      } as IUser,
    }));
  }

  amIAdminInProject(projectId: string): boolean {
    return !!this.authQuery.getProjectRoles()[projectId]?.find((c) => c === Object
      .keys(SystemRole)?.find((key) => SystemRole[key] === SystemRole.Admin));
  }
}
